import { UniAgFns } from "@/shared/uni-ag-fns";
import { GridReadyEvent } from "@ag-grid-community/core";
import { Component, Optional, ViewEncapsulation } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { InventoryDamageDialogComponent } from "app/inventory/inventory-damage-dialog.component";
import {
  AddonBin,
  AddonBinStationStatusEnum,
  AddonBinStatusEnum,
  ItemDetailAddon,
} from "app/model/entities/entity-model";
import { AddonUtilsService } from "app/shared/addon-utils.service";
import { AgFns, GridOptionsExt } from "app/shared/ag-fns";
import { DialogBaseComponent } from "app/shared/dialog-base.component";
import { DomainBaseComponent, wrapBusy } from "app/shared/domain-base.component";
import { DomainService } from "app/shared/domain.service";
import { FileViewDialogComponent, FileViewDialogParams, IDoc } from "app/shared/file-view-dialog.component";
import { NavFns } from "app/shared/nav-fns";
import { UnitOfWork } from "app/shared/unit-of-work";
import _ from "lodash";
import { takeUntil } from "rxjs/operators";
import { ProcessManagerComponent } from "./process-manager.component";

@Component({
  selector: "app-addon-bin-process",
  templateUrl: "./addon-bin-process.component.html",

  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class AddonBinProcessComponent extends DomainBaseComponent {
  addonBinId: number;
  addonBin: AddonBin;
  shouldShowAllItemDetailAddons = true;

  detailGridOptions: GridOptionsExt;
  itemDetailAddons: ItemDetailAddon[];

  AddonBinStationStatusEnum = AddonBinStationStatusEnum;
  badScans: string[] = [];

  constructor(
    public uow: UnitOfWork,
    protected domainService: DomainService,
    protected route: ActivatedRoute,
    protected addonUtilsService: AddonUtilsService,
    @Optional() private processManager: ProcessManagerComponent
  ) {
    super(domainService);

    this.route.paramMap.pipe(takeUntil(this.onDestroy)).subscribe(() => {
      this.updateFromContext();
    });

    // Subscribe to scanner results from parent
    this.processManager?.scannerResult$.pipe(takeUntil(this.onDestroy)).subscribe((result) => {
      this.onScanSuccess(result as ItemDetailAddon[]);
    });
  }

  override ngOnDestroy() {
    if (this.processManager) {
      this.processManager.showAddonBinScanner = false;
    }
    super.ngOnDestroy();
  }

  async canDeactivate() {
    const ok = await super.canDeactivate();
    if (!ok) return false;
    this.uow.clearEntities(AddonBin);
    this.uow.clearEntities(ItemDetailAddon);

    return true;
  }

  async updateFromContext() {
    this.addonBinId = +this.route.snapshot.params["addonBinId"];
    this.detailGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onDetailGridReady,
    });
    this.addonBin = await this.dbQueryService.getAddonBin(this.addonBinId);
    this.badScans = [];
    await this.refreshGrid();
    this.isPageReady = true;

    // Configure scanner in sidebar
    this.updateScannerVisibility();
  }

  private updateScannerVisibility() {
    if (this.processManager && this.canScanIntoStation()) {
      setTimeout(() => {
        this.processManager.showAddonBinScanner = true;
        this.processManager.addonBinScannerLabel = `Scan into '${this.addonBin?.pendingAddonStation?.name}'...`;
        this.processManager.scannerSearchFn = this.searchItemDetailForScan.bind(this);
        this.processManager.focusScanner();
      });
    } else if (this.processManager) {
      this.processManager.showAddonBinScanner = false;
    }
  }

  onDetailGridReady(event: GridReadyEvent) {
    const colDefs = [
      this.user.isBinProcessAdmin && {
        ...AgFns.createButtonProps("", this.markAsScanned.bind(this), {
          label: "Mark as Scanned",
          canDisplay: this.canMarkAsScanned.bind(this),
        }),
        colId: "canMarkAsScanned",
        ...AgFns.fixWidth(150),
      },
      { headerName: "Status", field: "addonBinStationStatus.name", ...AgFns.fixWidth(120) },
      {
        ...AgFns.createButtonProps("", this.markDamaged.bind(this), { label: "Mark Damaged" }),
        colId: "markDamaged",
        ...AgFns.fixWidth(130),
      },
      {
        headerName: "Inventory Identifier",
        field: "itemDetail.id",
        cellRenderer: "agGroupCellRenderer",
        cellClass: "ag-left-aligned-cell",
        ...AgFns.fixWidth(140),
      },
      { ...UniAgFns.getManufacturerColDef("itemDetail.product.productType.manufacturer.name") },
      { ...UniAgFns.getProductTypeSKUColDef("itemDetail.product.productType.style") },
      { ...UniAgFns.getProductTypeDescriptionColDef("itemDetail.product.productType.description") },
      { ...UniAgFns.getFeatureChoicesColDef("itemDetail.product.featureChoicesExtract") },

      { headerName: "Addon", field: "addon.nameAndLocation", ...AgFns.flexWidth(150) },
      { headerName: "Station", field: "addon.addonStation.name", ...AgFns.fixWidth(100) },
      { headerName: "Instructions", field: "addon.instructions", ...AgFns.flexWidth(150) },
      { headerName: "Addl. Info", field: "additionalInfo", ...AgFns.flexWidth(150) },
      {
        headerName: "Job Order",
        ...NavFns.createIdCellClickedNavProps("itemDetail.joDetail.joHeaderId", this.router, "/in-process/manage/job-orders"),
        ...AgFns.fixWidth(100),
      },
      // { headerName: "Job Order Detail", field: "itemDetail.joDetail.id" },
    ];

    const sortModel = [{ colId: "itemDetail.id", sort: "asc" as const }];

    AgFns.initGrid(event, colDefs, sortModel);
    AgFns.autoSizeAllColumns(this.detailGridOptions);
  }

  async refreshGrid() {
    this.itemDetailAddons = await this.dbQueryService.getItemDetailAddonsForAddonBin(this.addonBin.id);
    if (!this.shouldShowAllItemDetailAddons) {
      this.itemDetailAddons = this.itemDetailAddons.filter(
        (x) =>
          x.addon.addonStationId == this.addonBin.currentAddonStationId || x.addon.addonStationId == this.addonBin.pendingAddonStationId
      );
    }
    this.AgFns.refreshGrid(this.detailGridOptions, this.itemDetailAddons);

    // Update scanner visibility and refocus
    this.updateScannerVisibility();
  }

  hasAddonDocs(itdAddon: ItemDetailAddon) {
    return itdAddon?.addon.addonDocMaps.length > 0;
  }

  async viewAddonDocs(itdAddon: ItemDetailAddon) {
    const data: FileViewDialogParams = {
      header: "View Add-On Document",
      docs: itdAddon.addon.addonDocMaps.map((x) => x.addonDoc as IDoc),
    };
    await DialogBaseComponent.open(FileViewDialogComponent, this, data);
    // Return focus to scanner after dialog closes
    await this.UtilFns.wait(1);
    this.processManager?.focusScanner();
  }

  /**
   * Search callback for scanner input component.
   * Finds ItemDetailAddons by item detail ID for the pending station.
   */
  async searchItemDetailForScan(scannedValue: string): Promise<ItemDetailAddon[] | null> {
    const itdas = this.addonUtilsService.findScannableAddons(this.addonBin, this.itemDetailAddons, scannedValue);
    return itdas.length === 0 ? null : itdas;
  }

  /**
   * Handler for successful scan - processes the scanned item detail addons.
   */
  @wrapBusy
  async onScanSuccess(itdas: ItemDetailAddon[]) {
    if (!itdas || itdas.length === 0) return;
    await this.applyScan(itdas);
    this.toastr.success("The system scanned the item.");
  }

  private async applyScan(itdas: ItemDetailAddon[]) {
    this.addonUtilsService.applyScanIntoPendingStation(this.addonBin, this.itemDetailAddons, itdas);
    await this.dbSaveService.saveChanges();
    AgFns.refreshGrid(this.detailGridOptions, this.itemDetailAddons);
    this.updateScannerVisibility();
  }

  canMarkAsScanned(itda: ItemDetailAddon) {
    return this.user.isBinProcessAdmin && this.addonUtilsService.isAddonRowScannable(itda, this.addonBin);
  }

  async markAsScanned(itda: ItemDetailAddon) {
    const itdas = this.addonUtilsService.findScannableAddons(this.addonBin, this.itemDetailAddons, itda.itemDetailId);
    if (itdas.length > 0) {
      await this.onScanSuccess(itdas);
    }
  }

  @wrapBusy
  async scanAll() {
    const itdas = this.itemDetailAddons.filter((x) => this.addonUtilsService.isAddonRowScannable(x, this.addonBin));
    if (itdas.length === 0) {
      await this.dialogService.showOkMessage("No Items to Scan", "The items in this bin are complete at this station.");
      return;
    }
    await this.applyScan(itdas);
    this.toastr.success(`The system scanned ${new Set(itdas.map((x) => x.itemDetailId)).size} items.`);
  }

  canScanIntoStation() {
    return this.addonBin.pendingAddonStationId != null;
  }

  canMarkStationInProcess() {
    return (
      this.addonBin.currentAddonBinStationStatusId == AddonBinStationStatusEnum.Await ||
      this.addonBin.currentAddonBinStationStatusId == AddonBinStationStatusEnum.OnHold
    );
  }

  canMarkStationComplete() {
    return this.addonBin.currentAddonBinStationStatusId == AddonBinStationStatusEnum.InProcess;
  }

  canMarkStationOnHold() {
    return (
      this.addonBin.currentAddonBinStationStatusId == AddonBinStationStatusEnum.Await ||
      this.addonBin.currentAddonBinStationStatusId == AddonBinStationStatusEnum.InProcess
    );
  }

  canScanAll() {
    return this.authUser.isShippingAdmin && this.addonBin.currentAddonBinStationStatusId == null;
  }

  @wrapBusy
  async markStation(stationStatusCode: AddonBinStationStatusEnum) {
    this.addonBin.currentAddonBinStationStatusId = stationStatusCode;
    const itdas = this.itemDetailAddons.filter((x) => x.addon.addonStationId == this.addonBin.currentAddonStationId);
    itdas.forEach((x) => (x.addonBinStationStatusId = stationStatusCode));
    AgFns.refreshGrid(this.detailGridOptions, this.itemDetailAddons);
    if (stationStatusCode == AddonBinStationStatusEnum.Closed) {
      this.addonUtilsService.updatePendingAddonStation(this.addonBin);
    } else if (stationStatusCode == AddonBinStationStatusEnum.InProcess) {
      this.addonBin.addonBinStatusId = AddonBinStatusEnum.InProcess;
    } else if (stationStatusCode == AddonBinStationStatusEnum.OnHold) {
      this.addonBin.addonBinStatusId = AddonBinStatusEnum.OnHold;
    }
    await this.dbSaveService.saveChanges();
  }

  async markDamaged(itda: ItemDetailAddon) {
    const r = await DialogBaseComponent.open(InventoryDamageDialogComponent, this, {
      itemDetail: itda.itemDetail,
    });
    if (r) {
      this.refreshGrid();
    } else {
      // Return focus to scanner even if cancelled
      await this.UtilFns.wait(1);
      this.processManager?.focusScanner();
    }
  }

  isBinComplete() {
    return this.addonBin.currentAddonBinStationStatusId == AddonBinStationStatusEnum.Closed && this.addonBin.pendingAddonStationId == null;
  }

  private get activeItemDetailAddons() {
    if (!this.itemDetailAddons) return [];
    return this.itemDetailAddons.filter((x) => !x.entityAspect.entityState.isDeleted() && x.addon);
  }

  get binItemCount() {
    return new Set(this.activeItemDetailAddons.map((x) => x.itemDetailId)).size;
  }

  get pendingStationScannedCount() {
    if (!this.addonBin?.pendingAddonStationId) return 0;
    const pendingAddons = this.activeItemDetailAddons.filter(
      (x) => x.addon.addonStationId == this.addonBin.pendingAddonStationId
    );
    const byItemDetail = _.groupBy(pendingAddons, (x) => x.itemDetailId);
    return Object.values(byItemDetail).filter((addons) =>
      addons.every((a) => this.addonUtilsService.isAddonRowSettled(a))
    ).length;
  }

  get pendingStationRemainingCount() {
    if (!this.addonBin?.pendingAddonStationId) return 0;
    const pendingAddons = this.activeItemDetailAddons.filter(
      (x) => x.addon.addonStationId == this.addonBin.pendingAddonStationId
    );
    const byItemDetail = _.groupBy(pendingAddons, (x) => x.itemDetailId);
    return Object.values(byItemDetail).filter((addons) =>
      addons.some((a) => !this.addonUtilsService.isAddonRowSettled(a))
    ).length;
  }

  goShipPrep() {
    this.router.navigate(["/in-process/manage/scan-station"]);
  }
}
